import React, { useCallback, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Loader } from '@els/els-react--loader';
import { MetadataPropTypesShape } from '@Components/Results/MetadataPropTypes';
import PatentFamilyHeading from '@Containers/PatentFamilyClustering/Components/PatentFamilyHeading';
import PatentFamilyMember from '@Containers/PatentFamilyClustering/Components/PatentFamilyMember';
import PatentFamilyExpand from '@Containers/PatentFamilyClustering/Components/PatentFamilyExpand';
import PatentFamilyPagination from '@Containers/PatentFamilyClustering/Components/PatentFamilyPagination';
import { PAGE_LIMIT } from '@Containers/PatentFamilyClustering/constants';

import './PatentFamilyGroup.scss';

function PatentFamilyGroup({
  document,
  metadata,
  zoom,
  groupTitle,
  groupOffices,
  groupCount,
  pageNumber,
  isExpanded,
  isLoading,
  patentFamilyMembers,
  expandPatentFamilyGroup,
  collapsePatentFamilyGroup,
  setPageNumber,
  viewFullPatentFamily,
  getTrackingAttrs,
}) {
  const [mainDocumentFirstSectionExpanded, setMainDocumentFirstSectionExpanded] = useState(false);

  const onFamilyGroupTitleClick = useCallback(() => {
    setMainDocumentFirstSectionExpanded(!mainDocumentFirstSectionExpanded);
  }, [mainDocumentFirstSectionExpanded]);

  // Expanded / Collapsed slice
  const familyMembersSliced = useMemo(
    () => patentFamilyMembers && (isExpanded ? patentFamilyMembers : patentFamilyMembers.slice(0, 1)),
    [patentFamilyMembers, isExpanded]
  );

  return (
    <section className="patent-family-group u-els-margin-left-1x u-els-margin-top-1o2" role="group" aria-label="Patent Family">
      <PatentFamilyHeading
        metadata={metadata}
        document={document}
        title={groupTitle}
        offices={groupOffices}
        viewFullPatentFamily={viewFullPatentFamily}
        mainDocumentFirstSectionExpanded={mainDocumentFirstSectionExpanded}
        onFamilyGroupTitleClick={onFamilyGroupTitleClick}
        getTrackingAttrs={getTrackingAttrs}
      />
      {familyMembersSliced && (
        <div className="patent-family-group__members-list">
          {familyMembersSliced.map((familyMember) => {
            const isMainDocument = familyMember.id === document.id;
            return (
              <PatentFamilyMember
                key={familyMember.id}
                document={familyMember}
                metadata={metadata}
                zoom={zoom}
                isMainFamilyMember={isMainDocument}
                getTrackingAttrs={getTrackingAttrs}
                {...(isMainDocument && {
                  firstSectionExpanded: mainDocumentFirstSectionExpanded,
                  setFirstSectionExpanded: setMainDocumentFirstSectionExpanded,
                })}
              />
            );
          })}
        </div>
      )}
      {isLoading && <Loader isVisible isBlocking={false} className="patent-family-group__loader" />}
      {isExpanded && groupCount > PAGE_LIMIT ? (
        <PatentFamilyPagination
          document={document}
          metadata={metadata}
          groupCount={groupCount}
          pageNumber={pageNumber}
          setPageNumber={setPageNumber}
        />
      ) : null}
      <PatentFamilyExpand
        document={document}
        metadata={metadata}
        groupCount={groupCount}
        isExpanded={isExpanded}
        expandPatentFamilyGroup={expandPatentFamilyGroup}
        collapsePatentFamilyGroup={collapsePatentFamilyGroup}
      />
    </section>
  );
}

PatentFamilyGroup.propTypes = {
  // Own props
  metadata: MetadataPropTypesShape,
  document: PropTypes.object.isRequired,
  zoom: PropTypes.number,
  // Selectors
  groupTitle: PropTypes.string,
  groupOffices: PropTypes.array,
  groupCount: PropTypes.string,
  pageNumber: PropTypes.number,
  isExpanded: PropTypes.bool,
  isLoading: PropTypes.bool,
  patentFamilyMembers: PropTypes.array,
  // Actions
  expandPatentFamilyGroup: PropTypes.func.isRequired,
  collapsePatentFamilyGroup: PropTypes.func.isRequired,
  setPageNumber: PropTypes.func.isRequired,
  viewFullPatentFamily: PropTypes.func.isRequired,
  // From parent
  getTrackingAttrs: PropTypes.func.isRequired,
};

export default PatentFamilyGroup;
